'use client'

import {
  useEditor,
  EditorContent,
  Extension,
} from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import {
  TextStyle,
} from '@tiptap/extension-text-style'
import {
  Color,
} from '@tiptap/extension-color'
import TextAlign from '@tiptap/extension-text-align'
import {
  useEffect,
  useState,
} from 'react'
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Palette,
  Sparkles,
} from 'lucide-react'

import {
  Button,
} from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

const GradientText =
  Extension.create({
    name:
      'gradientText',

    addGlobalAttributes() {
      return [
        {
          types: [
            'textStyle',
          ],

          attributes: {
            gradient: {
              default:
                null,

              parseHTML:
                element =>
                  element.style
                    .background ||
                  null,

              renderHTML:
                attributes => {
                  if (
                    !attributes.gradient
                  ) {
                    return {}
                  }

                  return {
                    style:
                      `background: ${attributes.gradient}; ` +
                      '-webkit-background-clip: text; ' +
                      '-webkit-text-fill-color: transparent; ' +
                      'background-clip: text;',
                  }
                },
            },
          },
        },
      ]
    },
  })

interface RichTextEditorProps {
  content:
    string

  onChange:
    (
      content:
        string
    ) => void

  placeholder?:
    string
}

const gradientPresets = [
  {
    label:
      'Rosa',
    value:
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  },
  {
    label:
      'Púrpura',
    value:
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  {
    label:
      'Azul',
    value:
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  },
  {
    label:
      'Verde',
    value:
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  },
  {
    label:
      'Atardecer',
    value:
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  },
  {
    label:
      'Dorado',
    value:
      'linear-gradient(135deg, #ffd89b 0%, #19547b 100%)',
  },
  {
    label:
      'Coral',
    value:
      'linear-gradient(90deg, #ff0844 0%, #ffb199 100%)',
  },
]

const colorPresets = [
  '#000000',
  '#ffffff',
  '#667eea',
  '#f093fb',
  '#43e97b',
  '#fa709a',
  '#ff0844',
  '#ffd89b',
]

export function RichTextEditor({
  content,
  onChange,
  placeholder,
}: RichTextEditorProps) {
  const [
    showGradients,
    setShowGradients,
  ] =
    useState(false)

  const [
    showColors,
    setShowColors,
  ] =
    useState(false)

  const editor =
    useEditor({
      extensions: [
        StarterKit,
        Underline,
        TextStyle,
        Color,
        GradientText,

        TextAlign.configure({
          types: [
            'heading',
            'paragraph',
          ],
        }),
      ],

      content:
        content || '',

      onUpdate: ({
        editor,
      }) => {
        onChange(
          editor.getHTML()
        )
      },

      editorProps: {
        attributes: {
          class:
            [
              'prose',
              'prose-sm',
              'max-w-none',
              'min-h-[100px]',
              'p-4',
              'focus:outline-none',
              'text-foreground',
              'prose-headings:text-foreground',
              'prose-p:text-foreground',
              'prose-strong:text-foreground',
              'prose-li:text-foreground',
              'prose-blockquote:text-muted-foreground',
              'dark:prose-invert',
            ].join(
              ' '
            ),
        },
      },
    })

  /*
   * Tiptap toma `content` como valor inicial.
   * Cuando el contenido llega después de un fetch,
   * hay que sincronizarlo explícitamente.
   *
   * Esto también evita que un drawer que abre con
   * contenido vacío se quede vacío aunque el API
   * haya devuelto las instrucciones correctas.
   */
  useEffect(
    () => {
      if (!editor) {
        return
      }

      const nextContent =
        content || ''

      const currentContent =
        editor.getHTML()

      if (
        currentContent ===
        nextContent
      ) {
        return
      }

      editor.commands.setContent(
        nextContent,
        false
      )
    },
    [
      editor,
      content,
    ]
  )

  if (!editor) {
    return null
  }

  const applyGradient =
    (
      gradient:
        string
    ) => {
      editor
        .chain()
        .focus()
        .setMark(
          'textStyle',
          {
            gradient,
          }
        )
        .run()

      setShowGradients(
        false
      )
    }

  const applyColor =
    (
      color:
        string
    ) => {
      editor
        .chain()
        .focus()
        .setColor(
          color
        )
        .run()

      setShowColors(
        false
      )
    }

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-background text-foreground">
      <div className="flex flex-wrap gap-1 border-b border-border bg-muted/30 p-2">
        <Button
          type="button"
          size="sm"
          variant={
            editor.isActive(
              'bold'
            )
              ? 'default'
              : 'ghost'
          }
          onClick={() =>
            editor
              .chain()
              .focus()
              .toggleBold()
              .run()
          }
        >
          <Bold className="h-4 w-4" />
        </Button>

        <Button
          type="button"
          size="sm"
          variant={
            editor.isActive(
              'italic'
            )
              ? 'default'
              : 'ghost'
          }
          onClick={() =>
            editor
              .chain()
              .focus()
              .toggleItalic()
              .run()
          }
        >
          <Italic className="h-4 w-4" />
        </Button>

        <Button
          type="button"
          size="sm"
          variant={
            editor.isActive(
              'underline'
            )
              ? 'default'
              : 'ghost'
          }
          onClick={() =>
            editor
              .chain()
              .focus()
              .toggleUnderline()
              .run()
          }
        >
          <UnderlineIcon className="h-4 w-4" />
        </Button>

        <Button
          type="button"
          size="sm"
          variant={
            editor.isActive(
              'strike'
            )
              ? 'default'
              : 'ghost'
          }
          onClick={() =>
            editor
              .chain()
              .focus()
              .toggleStrike()
              .run()
          }
        >
          <Strikethrough className="h-4 w-4" />
        </Button>

        <div className="mx-1 h-6 w-px bg-border" />

        <Button
          type="button"
          size="sm"
          variant={
            editor.isActive(
              'heading',
              {
                level:
                  1,
              }
            )
              ? 'default'
              : 'ghost'
          }
          onClick={() =>
            editor
              .chain()
              .focus()
              .toggleHeading({
                level:
                  1,
              })
              .run()
          }
        >
          <Heading1 className="h-4 w-4" />
        </Button>

        <Button
          type="button"
          size="sm"
          variant={
            editor.isActive(
              'heading',
              {
                level:
                  2,
              }
            )
              ? 'default'
              : 'ghost'
          }
          onClick={() =>
            editor
              .chain()
              .focus()
              .toggleHeading({
                level:
                  2,
              })
              .run()
          }
        >
          <Heading2 className="h-4 w-4" />
        </Button>

        <Button
          type="button"
          size="sm"
          variant={
            editor.isActive(
              'heading',
              {
                level:
                  3,
              }
            )
              ? 'default'
              : 'ghost'
          }
          onClick={() =>
            editor
              .chain()
              .focus()
              .toggleHeading({
                level:
                  3,
              })
              .run()
          }
        >
          <Heading3 className="h-4 w-4" />
        </Button>

        <div className="mx-1 h-6 w-px bg-border" />

        <Button
          type="button"
          size="sm"
          variant={
            editor.isActive({
              textAlign:
                'left',
            })
              ? 'default'
              : 'ghost'
          }
          onClick={() =>
            editor
              .chain()
              .focus()
              .setTextAlign(
                'left'
              )
              .run()
          }
        >
          <AlignLeft className="h-4 w-4" />
        </Button>

        <Button
          type="button"
          size="sm"
          variant={
            editor.isActive({
              textAlign:
                'center',
            })
              ? 'default'
              : 'ghost'
          }
          onClick={() =>
            editor
              .chain()
              .focus()
              .setTextAlign(
                'center'
              )
              .run()
          }
        >
          <AlignCenter className="h-4 w-4" />
        </Button>

        <Button
          type="button"
          size="sm"
          variant={
            editor.isActive({
              textAlign:
                'right',
            })
              ? 'default'
              : 'ghost'
          }
          onClick={() =>
            editor
              .chain()
              .focus()
              .setTextAlign(
                'right'
              )
              .run()
          }
        >
          <AlignRight className="h-4 w-4" />
        </Button>

        <div className="mx-1 h-6 w-px bg-border" />

        <Popover
          open={
            showColors
          }
          onOpenChange={
            setShowColors
          }
        >
          <PopoverTrigger
            asChild
          >
            <Button
              type="button"
              size="sm"
              variant="ghost"
            >
              <Palette className="h-4 w-4" />
            </Button>
          </PopoverTrigger>

          <PopoverContent className="z-50 w-auto bg-background p-3">
            <div className="space-y-2">
              <p className="mb-2 text-xs font-medium text-foreground">
                Colores
              </p>

              <div className="grid grid-cols-4 gap-2">
                {colorPresets.map(
                  color => (
                    <button
                      type="button"
                      key={
                        color
                      }
                      className="h-8 w-8 rounded border-2 border-border transition-transform hover:scale-110"
                      style={{
                        backgroundColor:
                          color,
                      }}
                      onClick={() =>
                        applyColor(
                          color
                        )
                      }
                    />
                  )
                )}
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <Popover
          open={
            showGradients
          }
          onOpenChange={
            setShowGradients
          }
        >
          <PopoverTrigger
            asChild
          >
            <Button
              type="button"
              size="sm"
              variant="ghost"
            >
              <Sparkles className="h-4 w-4" />
            </Button>
          </PopoverTrigger>

          <PopoverContent className="z-50 w-64 bg-background p-3">
            <div className="space-y-2">
              <p className="mb-2 text-xs font-medium text-foreground">
                Degradados
              </p>

              <div className="grid gap-2">
                {gradientPresets.map(
                  gradient => (
                    <button
                      type="button"
                      key={
                        gradient.label
                      }
                      className="h-10 rounded border-2 border-border text-sm font-semibold transition-transform hover:scale-105"
                      style={{
                        background:
                          gradient.value,

                        WebkitBackgroundClip:
                          'text',

                        WebkitTextFillColor:
                          'transparent',

                        backgroundClip:
                          'text',
                      }}
                      onClick={() =>
                        applyGradient(
                          gradient.value
                        )
                      }
                    >
                      {
                        gradient.label
                      }
                    </button>
                  )
                )}
              </div>

              <Button
                type="button"
                size="sm"
                variant="outline"
                className="mt-2 w-full"
                onClick={() => {
                  editor
                    .chain()
                    .focus()
                    .unsetMark(
                      'textStyle'
                    )
                    .run()

                  setShowGradients(
                    false
                  )
                }}
              >
                Quitar estilo
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <EditorContent
        editor={
          editor
        }
        placeholder={
          placeholder
        }
      />
    </div>
  )
}
